import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ChevronDown, Download, Filter, Loader2, RefreshCw } from "lucide-react";
import { useGRCData } from "@/services/grc";
import { useCanExportGRCReport } from "@/hooks/usePermissions";
import { useCanAddMeeting, usePermissions } from "@/hooks/usePermissions";
import { GRCGrid } from "./components/GRCGrid";
import { MonthNav } from "./components/MonthNav";
import { GRCSearchBox } from "./components/GRCSearchBox";
import { GRCFilterPanel } from "./components/GRCFilterPanel";
import { GRCExportModal } from "./components/GRCExportModal";
import { SoftHoldsFlyout } from "./components/SoftHoldsFlyout";
import { CreateSoftHoldDialog } from "./components/CreateSoftHoldDialog";
import type { ProgramDetailDto, GRCActiveFilters, GRCSearchResult } from "@/types/grc";

const EMPTY_FILTERS: GRCActiveFilters = { eventTypes: [], statuses: [] };

export function GRCDashboardPage() {
  const navigate = useNavigate();
  const addMenuRef = useRef<HTMLDivElement>(null);
  const now = new Date();
  const [selectedDate, setSelectedDate] = useState(now);
  const [filters, setFilters] = useState<GRCActiveFilters>(EMPTY_FILTERS);
  const [showPickups, setShowPickups] = useState(false);
  const [showDatesAtBottom, setShowDatesAtBottom] = useState(true);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAddNewMenu, setShowAddNewMenu] = useState(false);
  const [showCreateSoftHoldDialog, setShowCreateSoftHoldDialog] = useState(false);
  const [selectedSoftHoldDetailsId, setSelectedSoftHoldDetailsId] = useState<number | null>(null);
  const [showSoftHoldsFlyout, setShowSoftHoldsFlyout] = useState(false);
  const [isSoftHoldsListOnlyMode, setIsSoftHoldsListOnlyMode] = useState(false);
  const [selectedSoftHoldId, setSelectedSoftHoldId] = useState(0);
  const [highlightName, setHighlightName] = useState<string | undefined>();

  const canExport = useCanExportGRCReport();
  const canAddMeeting = useCanAddMeeting();
  const { hasPermission } = usePermissions();
  const canViewToggleSettings = hasPermission("CanViewToggleSettings");
  const canAddSoftHold = hasPermission("CanAddSoftHold");
  const canViewSoftHold = hasPermission("CanViewSoftHold");

  const month = selectedDate.getMonth() + 1;
  const year = selectedDate.getFullYear();
  const monthLabel = format(selectedDate, "MMM yyyy");

  const { data, isPending, isFetching, refetch } = useGRCData(month, year);

  useEffect(() => {
    if (!showAddNewMenu) return;

    function handlePointerDown(event: PointerEvent) {
      if (!addMenuRef.current) return;
      if (!addMenuRef.current.contains(event.target as Node)) {
        setShowAddNewMenu(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setShowAddNewMenu(false);
      }
    }

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [showAddNewMenu]);

  const draftCalendarItems = useMemo(() => {
    if (!data) return [];

    const seen = new Set<number>();
    return data.programDetails.filter((program) => {
      if (!program.isDraft || program.softHoldId <= 0) return false;
      if (seen.has(program.softHoldId)) return false;
      seen.add(program.softHoldId);
      return true;
    });
  }, [data]);

  // ── Navigation ──────────────────────────────────────────────────────────────

  const goToPrevMonth = useCallback(() => {
    setSelectedDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  }, []);

  const goToNextMonth = useCallback(() => {
    setSelectedDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  }, []);

  const goToThisMonth = useCallback(() => {
    setSelectedDate(new Date());
  }, []);

  // ── Search handlers ─────────────────────────────────────────────────────────

  function handleSearchSelect(result: GRCSearchResult) {
    // Navigate to the month of the result's start date and highlight
    const start = new Date(result.eventStartDate);
    setSelectedDate(new Date(start.getFullYear(), start.getMonth(), 1));
    setHighlightName(result.eventName);
  }

  function handleSearchClear() {
    setHighlightName(undefined);
  }

  // ── Row interactions ────────────────────────────────────────────────────────

  function handleEventNameDoubleClick(prog: ProgramDetailDto) {
    if (prog.isSoftHold && prog.softHoldId > 0) {
      setIsSoftHoldsListOnlyMode(false);
      setSelectedSoftHoldId(prog.softHoldId);
      setShowSoftHoldsFlyout(true);
    } else if (prog.isDraft && prog.softHoldId > 0) {
      navigate(`/draftcalendar?meetingId=${prog.softHoldId}&mode=view`);
    }
  }

  function handleDraftEventClick(prog: ProgramDetailDto) {
    if (prog.isDraft && prog.softHoldId > 0) {
      navigate(`/draftcalendar?meetingId=${prog.softHoldId}&mode=edit`);
    }
  }

  function handleDraftItemFromFlyout(draftMeetingId: number) {
    if (draftMeetingId <= 0) return;
    setShowSoftHoldsFlyout(false);
    navigate(`/draftcalendar?meetingId=${draftMeetingId}&mode=view`);
  }

  function handleOpenSoftHoldsList() {
    if (!data || data.totalSoftHoldAndDraftCount <= 0) return;
    setIsSoftHoldsListOnlyMode(true);
    setSelectedSoftHoldId(data?.programDetails.find((item) => item.isSoftHold && item.softHoldId > 0)?.softHoldId ?? 0);
    setShowSoftHoldsFlyout(true);
  }

  function handleSoftHoldItemFromList(softHoldId: number) {
    if (softHoldId <= 0) return;
    setShowSoftHoldsFlyout(false);
    setSelectedSoftHoldDetailsId(softHoldId);
    setShowCreateSoftHoldDialog(true);
  }

  // ── Status bar content ──────────────────────────────────────────────────────

  const hasFilters = filters.eventTypes.length > 0 || filters.statuses.length > 0;

  return (
    <main className="flex flex-col gap-0" aria-label="GRC Dashboard">
      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
        {/* Month navigation */}
        <MonthNav
          label={monthLabel}
          onPrev={goToPrevMonth}
          onNext={goToNextMonth}
          showThisMonth
          onThisMonth={goToThisMonth}
        />

        <div className="mx-2 h-6 w-px bg-slate-200" aria-hidden="true" />

        {/* Search */}
        <GRCSearchBox onSelect={handleSearchSelect} onClear={handleSearchClear} />

        <div className="ml-auto flex items-center gap-2">
          {/* Show Dates At Bottom toggle */}
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-700 select-none">
            <input
              type="checkbox"
              checked={showDatesAtBottom}
              onChange={(e) => setShowDatesAtBottom(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            Show Dates at Bottom
          </label>

          {/* Show Pickups toggle */}
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-700 select-none">
            <input
              type="checkbox"
              checked={showPickups}
              onChange={(e) => setShowPickups(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            Show Pickups
          </label>

          {/* Refresh */}
          <button
            type="button"
            onClick={() => void refetch()}
            aria-label="Refresh data"
            className="flex h-8 w-8 items-center justify-center rounded border border-slate-300 text-slate-600 hover:bg-slate-100"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>

          {/* Filter */}
          <button
            type="button"
            onClick={() => setShowFilterPanel(true)}
            aria-label="Open filters"
            className={`flex h-8 w-8 items-center justify-center rounded border ${
              hasFilters
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-300 text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Filter className="h-4 w-4" />
          </button>

          {/* Export */}
          <button
            type="button"
            disabled={!canExport}
            onClick={() => setShowExportModal(true)}
            aria-label="Export GRC report"
            className="flex h-8 w-8 items-center justify-center rounded border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Download className="h-4 w-4" />
          </button>

          {/* Add New */}
          {canViewToggleSettings && (
            <div ref={addMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setShowAddNewMenu((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={showAddNewMenu}
                className="inline-flex items-center gap-1 rounded border border-slate-900 bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
              >
                Add New
                <ChevronDown className="h-4 w-4" />
              </button>

              {showAddNewMenu && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-40 mt-1 w-64 rounded border border-slate-200 bg-white py-1 shadow-lg"
                >
                  <button
                    type="button"
                    role="menuitem"
                    disabled={!canAddSoftHold}
                    onClick={() => {
                      setShowAddNewMenu(false);
                      setShowCreateSoftHoldDialog(true);
                    }}
                    className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Add Soft Hold to GRC
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={!canAddMeeting}
                    onClick={() => {
                      setShowAddNewMenu(false);
                      navigate("/draftcalendar?create=true");
                    }}
                    className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Add Meeting to Draft Calendar
                  </button>
                </div>
              )}
            </div>
          )}

          {canViewSoftHold && (
            <button
              type="button"
              onClick={handleOpenSoftHoldsList}
              aria-label="Open soft holds list"
              disabled={!data || data.totalSoftHoldAndDraftCount <= 0}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-none border border-black bg-black text-sm font-normal leading-5 text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
            >
              {data?.totalSoftHoldAndDraftCount ?? 0}
            </button>
          )}
        </div>
      </div>

      {/* ── Summary stats bar ── */}
      {data && (
        <div className="flex items-center gap-6 border-b border-slate-100 bg-slate-50 px-4 py-1.5 text-xs text-slate-600">
          <span>Soft Holds: <strong className="text-slate-900">{data.softHoldsCount}</strong></span>
          <span>Core Drafts: <strong className="text-slate-900">{data.coreDraftMeetingsCount}</strong></span>
          <span>Secondary Drafts: <strong className="text-slate-900">{data.secondaryDraftMeetingsCount}</strong></span>
          <span>DU Capacity: <strong className="text-slate-900">{data.duRoomCapacity}</strong></span>
          {data.lastUpdateDisplay && (
            <span className="ml-auto text-slate-400">Updated: {data.lastUpdateDisplay}</span>
          )}
        </div>
      )}

      {/* ── Content ── */}
      <div className="flex-1 overflow-hidden px-3 pt-2">
        {isPending && !data ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            <span className="ml-3 text-sm text-slate-500">Loading GRC data…</span>
          </div>
        ) : !data || data.programDetails.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-500">
            No program data available for {monthLabel}.
          </p>
        ) : (
          <GRCGrid
            data={data}
            filters={filters}
            showPickups={showPickups}
            showDatesAtBottom={showDatesAtBottom}
            highlightEventName={highlightName}
            onEventNameDoubleClick={handleEventNameDoubleClick}
            onDraftEventClick={handleDraftEventClick}
          />
        )}
      </div>

      {/* ── Panels / Modals ── */}
      {showFilterPanel && (
        <GRCFilterPanel
          data={data}
          filters={filters}
          onFiltersChange={setFilters}
          onClose={() => setShowFilterPanel(false)}
        />
      )}

      {showExportModal && (
        <GRCExportModal
          month={month}
          year={year}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {showSoftHoldsFlyout && (
        <SoftHoldsFlyout
          month={month}
          year={year}
          initialSoftHoldId={selectedSoftHoldId}
          listOnly={isSoftHoldsListOnlyMode}
          includeDraftItems={!isSoftHoldsListOnlyMode}
          onSoftHoldItemClick={handleSoftHoldItemFromList}
          draftItems={draftCalendarItems}
          onDraftItemClick={handleDraftItemFromFlyout}
          onClose={() => setShowSoftHoldsFlyout(false)}
        />
      )}

      {showCreateSoftHoldDialog && (
        <CreateSoftHoldDialog
          month={month}
          year={year}
          softHoldId={selectedSoftHoldDetailsId ?? undefined}
          onCreated={() => {
            setSelectedSoftHoldId(0);
            setSelectedSoftHoldDetailsId(null);
          }}
          onClose={() => {
            setShowCreateSoftHoldDialog(false);
            setSelectedSoftHoldDetailsId(null);
          }}
        />
      )}
    </main>
  );
}
